import {
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { useUpdate } from "@refinedev/core";
import UsersEmailSelection from "components/inputs/fund-users-email-drop-down";
import { IGrantRequest, OnTimeSchedule } from "interfaces/grant-requests";
import { IUser } from "interfaces/user";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

type GrantStatus = "PENDING" | "APPROVED" | "REJECTED" | "PROCESSED";

interface ModalProps {
  visible: boolean;
  data: {
    grantRequest: IGrantRequest | undefined;
    value: GrantStatus;
  };
  close: React.Dispatch<React.SetStateAction<boolean>>;
}

export const EvaluateGrantRequestModal: React.FC<ModalProps> = ({
  visible,
  close,
  data,
}) => {
  const [rejectionReason, setRejectionReason] = useState("");
  const [error, setError] = useState(false);
  const [pendingRequestError, setPendingRequestError] = useState("");
  const [executionDate, setExecutionDate] = useState({
    value: new Date().toISOString().substring(0, 10),
    error: false,
  });

  const [selectedUsers, setSelectedUsers] = useState<IUser[]>([]);
  const [existingEmails, setExistingEmails] = useState<string[]>([]);
  const { mutate, isLoading } = useUpdate();
  const navigate = useNavigate();

  // Status configuration
  const statusConfig = {
    APPROVED: { label: "Accept", color: "success" },
    REJECTED: { label: "Reject", color: "error" },
    PROCESSED: { label: "Process", color: "primary" },
  } as const;

  const getValuesForStatus = (
    status: GrantStatus,
    rejectionReason?: string
  ) => {
    switch (status) {
      case "APPROVED":
        return {
          status: status,
          emailsToNotify: existingEmails,
        };
      case "PROCESSED":
        return {
          status: status,
          executionDate: executionDate.value,
          emailsToNotify: existingEmails,
        };
      case "REJECTED":
        return {
          rejectionReason: rejectionReason,
          status: status,
          emailsToNotify: existingEmails,
        };
      default:
        return {
          emailsToNotify: existingEmails,
          status: status,
        };
    }
  };

  const getNavigationPath = (
    status: GrantStatus,
    grantRequest: IGrantRequest
  ) => {
    switch (status) {
      case "APPROVED":
        const isScheduled =
          (grantRequest?.type === "ONETIME" &&
            !(grantRequest?.scheduleInformation as OnTimeSchedule).now) ||
          grantRequest?.type === "RECURRENT";
        return isScheduled
          ? "/grant-requests/status/scheduled"
          : "/grant-requests/status/approved";
      case "PROCESSED":
        return "/grant-requests/status/processed";
      case "REJECTED":
        return "/grant-requests/status/rejected";
      default:
        return "/";
    }
  };

  const handleSubmit = () => {
    if (data.value === "REJECTED" && !rejectionReason) {
      setError(true);
      return;
    }

    setPendingRequestError("");
    mutate(
      {
        resource: "grant-requests",
        values: getValuesForStatus(data.value, rejectionReason),
        id: data.grantRequest?.grantRequestId as any,
      },
      {
        onSuccess: () => {
          const navigationPath = getNavigationPath(
            data.value,
            data.grantRequest!
          );
          navigate(navigationPath);
          handleClose();
        },
        onError: (error) => {
          setPendingRequestError(error.message);
        },
      }
    );
  };

  const handleClose = () => {
    close(false);
    setError(false);
    setPendingRequestError("");
    setRejectionReason("");
  };

  const currentConfig = statusConfig[data.value as keyof typeof statusConfig];
  const actionLabel = currentConfig?.label || "Process";

  return (
    <Dialog open={visible} onClose={handleClose}>
      <DialogTitle color="primary">
        {actionLabel} this grant request
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography>
          Are you sure you want to {actionLabel.toLowerCase()} this user grant
          request?
        </Typography>

        {data.value === "REJECTED" && (
          <TextField
            value={rejectionReason}
            error={error}
            helperText={error && "The rejection reason is required"}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            label="Rejection reason"
            name="rejectionReason"
            multiline
            rows={3}
            onChange={(e) => {
              setError(false);
              setRejectionReason(e.target.value);
            }}
          />
        )}

        {data.value === "PROCESSED" && (
          <TextField
            id="executionDate"
            name="executionDate"
            error={executionDate.error}
            helperText={
              executionDate.error &&
              "The date is required, and cannot be negative value!"
            }
            defaultValue={executionDate.value}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="date"
            label="Execution Date"
            onChange={(event) => {
              setExecutionDate({
                value: event.target.value,
                error: false,
              });
            }}
          />
        )}
        {data.grantRequest && (
          <Grid container spacing={1} pt={1}>
            <UsersEmailSelection
              key={existingEmails.join(",")}
              fundAccountId={
                data.grantRequest?.fundAccount.fundAccountId as number
              }
              value={selectedUsers}
              setSelectedUser={setSelectedUsers}
              showError={undefined}
              filterEmails={data.grantRequest?.emailToNotify ?? []}
              selectedAccount={data.grantRequest?.fundAccount.fundAccountId}
              onFilterEmailsChange={setExistingEmails}
              onEmailsChange={(emails) => setExistingEmails(emails)}
            />
          </Grid>
        )}

        {pendingRequestError && (
          <Alert sx={{ mt: 1 }} severity="error">
            {pendingRequestError}
          </Alert>
        )}
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading}
          startIcon={
            isLoading && <CircularProgress size={20} color="inherit" />
          }
        >
          Confirm
        </Button>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
